import { v4 as uuidv4 } from "uuid";
import { useListenFromRenderer } from "../message";
import { postToRenderer } from "../message/utils";
import { fetchGatewayInferenceJob, fetchInferenceJob } from "./shared/fetch-inference-job";
import { getTargetEditorImage } from "./shared/target-editor-image";

const TOOL_NAME = "Pose Matcher";
const GPT_PROMPT_DOCUMENT_ID = "ckol2i7cir0up23ber2tolcj";

const callPoseMatcher = async (
	base64Ref: string,
	messageId: string,
	jwt: string,
	tool_document_id: string,
) => {
	const { success } = await getTargetEditorImage({
		commandName: TOOL_NAME,
		resultLayerName: TOOL_NAME,
		processImage: async ({ imageBase64, executionContext }) => {
			const data = {
				inputs: {
					$source_img: imageBase64,
					$target_img: base64Ref,
				},
				jwt,
				tool_document_id,
			};
			return fetchPoseMatcher(data, executionContext, messageId);
		},
	});
	return success;
};

const callPoseMatcherGPT = async (
	base64Ref: string,
	messageId: string,
	jwt: string,
	tool_document_id: string,
	batch_size: number,
) => {
	const { success } = await getTargetEditorImage({
		commandName: TOOL_NAME,
		resultLayerName: TOOL_NAME,
		processImage: async ({ imageBase64, executionContext }) => {
			const data = {
				prompt_document_id: GPT_PROMPT_DOCUMENT_ID,
				prompt_index: 0,
				input_images: [imageBase64, base64Ref],
				from_editor: true,
				jwt,
				tool_document_id,
				batch_size,
			};
			return fetchPoseMatcherGPT(data, executionContext, messageId);
		},
	});
	return success;
};

export async function fetchPoseMatcher(
	params: {
		inputs: {
			$source_img: string;
			$target_img: string;
		};
		jwt: string;
		tool_document_id: string;
	},
	executionContext: any,
	messageId: string,
): Promise<string | false> {
	return fetchGatewayInferenceJob(params, executionContext, messageId);
}

export async function fetchPoseMatcherGPT(
	params: {
		prompt_document_id: string;
		prompt_index: number;
		input_images: string[];
		from_editor: boolean;
		jwt: string;
		tool_document_id: string;
	},
	executionContext: any,
	messageId: string,
): Promise<string | string[] | false> {
	return fetchInferenceJob({
		params,
		executionContext,
		messageId,
		apiPath: "/infer/oplux",
		statusEndpoint: (apiUrl, jobId) =>
			`${apiUrl.replace("/infer/oplux", "")}/status/${jobId}`,
		resultEndpoint: (apiUrl, jobId) =>
			`${apiUrl.replace("/infer/oplux", "")}/result/${jobId}`,
		cancelEndpoint: (apiUrl, jobId) => ({
			url: `${apiUrl.replace("/infer/oplux", "")}/cancel/${jobId}`,
			method: "POST",
		}),
		extractResult: (data) => data.image_base64_list ?? [data.image_base64],
		useGatewayUrl: true,
	});
}

export function useListenPoseMatcherMessage() {
	useListenFromRenderer([
		{
			key: "poseMatcher",
			onMessage: async (message) => {
				const success = await callPoseMatcher(
					message.values.base64Ref,
					message.id,
					message.values.jwt,
					message.values.tool_document_id,
				);
				postToRenderer([
					{
						key: "finish",
						id: uuidv4(),
						values: {
							id: message.id,
							status: success ? "success" : "error",
						},
					},
				]);
			},
		},
		{
			key: "poseMatcherGPT",
			onMessage: async (message) => {
				const success = await callPoseMatcherGPT(
					message.values.base64Ref,
					message.id,
					message.values.jwt,
					message.values.tool_document_id,
					message.values.batch_size,
				);
				postToRenderer([
					{
						key: "finish",
						id: uuidv4(),
						values: {
							id: message.id,
							status: success ? "success" : "error",
						},
					},
				]);
			},
		},
	]);
}
